<script setup lang="ts">
// Emit function to send question to chat
const emit = defineEmits<{
  sendQuestion: [question: string]
}>()

// Function to handle question click
function handleQuestionClick(question: string) {
  emit('sendQuestion', question)
}
</script>

<template>
  <div class="faq-container">
    <div class="faq-content">
      <div class="faq-header">
        <h2 class="faq-title">
          What can I help you with?
        </h2>
        <p class="faq-subtitle">
          Click any question below to ask me directly:
        </p>
      </div>

      <div class="faq-categories">
        <!-- System Health & Problems -->
        <div class="faq-category">
          <div class="faq-category-header">
            <div class="faq-icon">
              🚨
            </div>
            <h3 class="faq-category-title">
              System Health & Problems
            </h3>
          </div>
          <div class="faq-items">
            <button class="faq-button" @click="handleQuestionClick('What problems do we have right now?')">
              What problems do we have right now?
            </button>
            <button class="faq-button" @click="handleQuestionClick('Show me critical problems')">
              Show me critical problems
            </button>
            <button class="faq-button" @click="handleQuestionClick('What problems happened in the last hour?')">
              What problems happened in the last hour?
            </button>
            <button class="faq-button" @click="handleQuestionClick('Which hosts are offline?')">
              Which hosts are offline?
            </button>
          </div>
        </div>

        <!-- Resource Monitoring -->
        <div class="faq-category">
          <div class="faq-category-header">
            <div class="faq-icon">
              📊
            </div>
            <h3 class="faq-category-title">
              Resource Monitoring
            </h3>
          </div>
          <div class="faq-items">
            <button class="faq-button" @click="handleQuestionClick('Which hosts are running out of CPU?')">
              Which hosts are running out of CPU?
            </button>
            <button class="faq-button" @click="handleQuestionClick('Which hosts are running out of memory?')">
              Which hosts are running out of memory?
            </button>
            <button class="faq-button" @click="handleQuestionClick('Which hosts are running out of disk space?')">
              Which hosts are running out of disk space?
            </button>
            <button class="faq-button" @click="handleQuestionClick('Which hosts are running out of resources?')">
              Which hosts are running out of resources?
            </button>
          </div>
        </div>

        <!-- Host Information -->
        <div class="faq-category">
          <div class="faq-category-header">
            <div class="faq-icon">
              🖥️
            </div>
            <h3 class="faq-category-title">
              Host Information
            </h3>
          </div>
          <div class="faq-items">
            <button class="faq-button" @click="handleQuestionClick('Show me all hosts')">
              Show me all hosts
            </button>
            <button class="faq-button" @click="handleQuestionClick('Which hosts are up?')">
              Which hosts are up?
            </button>
            <button class="faq-button" @click="handleQuestionClick('Show me host uptime')">
              Show me host uptime
            </button>
            <button class="faq-button" @click="handleQuestionClick('Show me recently rebooted hosts')">
              Show me recently rebooted hosts
            </button>
            <button class="faq-button" @click="handleQuestionClick('Which hosts are in maintenance?')">
              Which hosts are in maintenance?
            </button>
          </div>
        </div>

        <!-- Host Groups -->
        <div class="faq-category">
          <div class="faq-category-header">
            <div class="faq-icon">
              📁
            </div>
            <h3 class="faq-category-title">
              Host Groups
            </h3>
          </div>
          <div class="faq-items">
            <button class="faq-button" @click="handleQuestionClick('Show me all host groups')">
              Show me all host groups
            </button>
            <button class="faq-button" @click="handleQuestionClick('What host groups do we have?')">
              What host groups do we have?
            </button>
          </div>
        </div>

        <!-- SLA & Availability Reports -->
        <div class="faq-category">
          <div class="faq-category-header">
            <div class="faq-icon">
              📈
            </div>
            <h3 class="faq-category-title">
              SLA & Availability Reports
            </h3>
          </div>
          <div class="faq-items">
            <button class="faq-button" @click="handleQuestionClick('Generate SLA report for last 7 days')">
              Generate SLA report for last 7 days
            </button>
            <button class="faq-button" @click="handleQuestionClick('Generate SLA report for last 30 days')">
              Generate SLA report for last 30 days
            </button>
            <button class="faq-button" @click="handleQuestionClick('Generate SLA report for current month')">
              Generate SLA report for current month
            </button>
            <button class="faq-button" @click="handleQuestionClick('Generate SLA report for 6 months')">
              Generate SLA report for 6 months
            </button>
            <button class="faq-button" @click="handleQuestionClick('Generate SLA report for 1 year')">
              Generate SLA report for 1 year
            </button>
            <button class="faq-button" @click="handleQuestionClick('Generate SLA report for all time')">
              Generate SLA report for all time
            </button>
          </div>
        </div>

        <!-- Network & Connectivity -->
        <div class="faq-category">
          <div class="faq-category-header">
            <div class="faq-icon">
              🌐
            </div>
            <h3 class="faq-category-title">
              Network & Connectivity
            </h3>
          </div>
          <div class="faq-items">
            <button disabled class="faq-button" @click="handleQuestionClick('Ping server.org')">
              Ping server.org
            </button>
            <button class="faq-button" @click="handleQuestionClick('Show me proxy status')">
              Show me proxy status
            </button>
            <button class="faq-button" @click="handleQuestionClick('List all proxies')">
              List all proxies
            </button>
            <button class="faq-button" @click="handleQuestionClick('What is the status of our proxies?')">
              What is the status of our proxies?
            </button>
          </div>
        </div>
      </div>

      <div class="faq-footer">
        <div class="faq-tips">
          <h4 class="faq-tips-title">
            💡 Tips for better results:
          </h4>
          <ul class="faq-tips-list">
            <li>Be specific about what you want to know</li>
            <li>Ask for "more" or "show all" to see additional results</li>
            <li>I can filter by severity levels (1-5) and time ranges</li>
            <li>You can ask follow-up questions for more detailed information</li>
          </ul>
        </div>

        <div class="faq-disclaimer">
          <h4 class="faq-disclaimer-title">
            ⚠️ Important Note:
          </h4>
          <p class="faq-disclaimer-text">
            I provide read-only monitoring information and insights. I don't perform any management actions,
            configuration changes, or system modifications. All responses are for information and analysis purposes
            only.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.faq-container {
  height: 100%;
  overflow-y: auto;
  background-color: #f8f9fa;
}

.faq-content {
  padding: 20px;
}

.faq-header {
  text-align: center;
  margin-bottom: 24px;
}

.faq-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.faq-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.faq-categories {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.faq-category {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.faq-category-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.faq-icon {
  font-size: 20px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 8px;
}

.faq-category-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.faq-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.faq-button {
  display: block;
  width: 100%;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #0072A3;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}

.faq-button:hover {
  background: #e5f3ff;
  border-color: #0072A3;
  transform: translateX(2px);
}

.faq-button:active {
  transform: translateX(1px);
  background: #cce7ff;
}

.faq-footer {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.faq-tips {
  background: #f0f9ff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #0ea5e9;
}

.faq-tips-title {
  font-size: 14px;
  font-weight: 600;
  color: #0369a1;
  margin: 0 0 8px 0;
}

.faq-tips-list {
  margin: 0;
  padding-left: 16px;
  color: #0369a1;
}

.faq-tips-list li {
  font-size: 12px;
  margin-bottom: 4px;
}

.faq-tips-list li:last-child {
  margin-bottom: 0;
}

.faq-disclaimer {
  background: #fef3c7;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #f59e0b;
  margin-top: 16px;
}

.faq-disclaimer-title {
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 8px 0;
}

.faq-disclaimer-text {
  font-size: 12px;
  color: #92400e;
  margin: 0;
  line-height: 1.4;
}
</style>
